import { db } from '../firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  setDoc,
  query, 
  where, 
  orderBy,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';

// Products Service
export const ProductsService = {
  async getAll() {
    try {
      const productsRef = collection(db, 'products');
      const q = query(productsRef, where('active', '==', true));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  },

  async getById(productId) {
    try {
      const productRef = doc(db, 'products', productId);
      const productSnap = await getDoc(productRef);
      if (productSnap.exists()) {
        return { id: productSnap.id, ...productSnap.data() };
      }
      throw new Error('Product not found');
    } catch (error) {
      console.error('Error fetching product:', error);
      throw error;
    }
  },

  async create(productData) {
    try {
      const productsRef = collection(db, 'products');
      const docRef = await addDoc(productsRef, {
        ...productData,
        active: true,
        createdAt: serverTimestamp()
      });
      return { id: docRef.id, ...productData };
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  },

  async update(productId, productData) {
    try {
      const productRef = doc(db, 'products', productId);
      await updateDoc(productRef, {
        ...productData,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  },

  async delete(productId) {
    try {
      const productRef = doc(db, 'products', productId);
      await deleteDoc(productRef);
      return { success: true };
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  }
};

// Orders Service
export const OrdersService = {
  async create(orderData) {
    try {
      const ordersRef = collection(db, 'orders');
      const orderId = 'ORD' + Date.now();
      const docRef = await addDoc(ordersRef, {
        orderId,
        ...orderData,
        orderStatus: 'Pending',
        paymentStatus: 'paid',
        createdAt: serverTimestamp()
      });
      return { id: docRef.id, orderId, ...orderData };
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  },

  async getAll() {
    try {
      const ordersRef = collection(db, 'orders');
      const q = query(ordersRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      }));
    } catch (error) {
      console.error('Error fetching orders:', error);
      throw error;
    }
  },

  async getById(orderId) {
    try {
      const ordersRef = collection(db, 'orders');
      const q = query(ordersRef, where('orderId', '==', orderId));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const orderData = doc.data();
        return { 
          id: doc.id, 
          ...orderData,
          createdAt: orderData.createdAt?.toDate?.() || new Date()
        };
      }
      throw new Error('Order not found');
    } catch (error) {
      console.error('Error fetching order:', error);
      throw error;
    }
  },

  async updateStatus(orderId, status) {
    try {
      const ordersRef = collection(db, 'orders');
      const q = query(ordersRef, where('orderId', '==', orderId));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const orderDoc = querySnapshot.docs[0];
        await updateDoc(orderDoc.ref, {
          orderStatus: status
        });
        return { success: true };
      }
      throw new Error('Order not found');
    } catch (error) {
      console.error('Error updating order status:', error);
      throw error;
    }
  },

  async update(orderId, orderData) {
    try {
      const ordersRef = collection(db, 'orders');
      const q = query(ordersRef, where('orderId', '==', orderId));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const orderDoc = querySnapshot.docs[0];
        await updateDoc(orderDoc.ref, {
          ...orderData,
          updatedAt: serverTimestamp()
        });
        return { success: true };
      }
      throw new Error('Order not found');
    } catch (error) {
      console.error('Error updating order:', error);
      throw error;
    }
  },

  async delete(orderId) {
    try {
      const ordersRef = collection(db, 'orders');
      const q = query(ordersRef, where('orderId', '==', orderId));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const orderDoc = querySnapshot.docs[0];
        await deleteDoc(orderDoc.ref);
        return { success: true };
      }
      throw new Error('Order not found');
    } catch (error) {
      console.error('Error deleting order:', error);
      throw error;
    }
  }
};

// Analytics Service
export const AnalyticsService = {
  async getStats() {
    try {
      // Get all orders
      const ordersRef = collection(db, 'orders');
      const ordersSnapshot = await getDocs(ordersRef);
      const orders = ordersSnapshot.docs.map(doc => doc.data());
      
      // Calculate stats
      const totalOrders = orders.length;
      const totalRevenue = orders.reduce((sum, order) => sum + (order.total || 0), 0);
      
      // Get unique customers
      const uniqueCustomers = new Set(orders.map(order => order.customer?.email)).size;
      
      // Get products count
      const productsRef = collection(db, 'products');
      const productsSnapshot = await getDocs(query(productsRef, where('active', '==', true)));
      const totalProducts = productsSnapshot.size;
      
      return {
        totalOrders,
        totalRevenue,
        totalCustomers: uniqueCustomers,
        totalProducts
      };
    } catch (error) {
      console.error('Error fetching analytics:', error);
      throw error;
    }
  }
};

// Users Service
export const UsersService = {
  async create(userData) {
    try {
      const usersRef = collection(db, 'users');
      const docRef = await addDoc(usersRef, {
        ...userData,
        createdAt: serverTimestamp()
      });
      return { id: docRef.id, ...userData };
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  },

  async getByUserId(userId) {
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('userId', '==', userId));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...doc.data() };
      }
      throw new Error('User profile not found');
    } catch (error) {
      console.error('Error fetching user:', error);
      throw error;
    }
  },

  async update(docId, userData) {
    try {
      const userRef = doc(db, 'users', docId);
      await updateDoc(userRef, userData);
      return { success: true };
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }
};

// Carts Service
export const CartsService = {
  async getByUserId(userId) {
    try {
      const cartRef = doc(db, 'carts', userId);
      const cartSnap = await getDoc(cartRef);
      if (cartSnap.exists()) {
        return cartSnap.data().items || [];
      }
      return [];
    } catch (error) {
      console.error('Error fetching cart:', error);
      throw error;
    }
  },

  async save(userId, items) {
    try {
      const cartRef = doc(db, 'carts', userId);
      await setDoc(cartRef, {
        items,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error saving cart:', error);
      throw error;
    }
  },

  async clear(userId) {
    try {
      const cartRef = doc(db, 'carts', userId);
      await setDoc(cartRef, {
        items: [],
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error clearing cart:', error);
      throw error;
    }
  }
};
